import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ArrowUpRight, MapPin, Quote, ChevronDown } from "lucide-react";
import { Reveal, MaskLines } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { VideoReel } from "@/components/site/VideoReel";
import { LeadForm } from "@/components/site/LeadForm";
import { STEPS, EVENT_TYPES, PACKAGES, FAQS, SERVICE_AREAS } from "@/data/content";
import { MEDIA } from "@/data/media";

const HERO_IMG = "https://images.unsplash.com/photo-1723373457175-31b09fa7d405?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const EVENT_IMG = "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

function TiltFrame() {
  const [t, setT] = useState({ rx: 0, ry: 0 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ rx: py * -8, ry: px * 8 });
  };
  return (
    <motion.div
      className="relative"
      onMouseMove={onMove}
      onMouseLeave={() => setT({ rx: 0, ry: 0 })}
      style={{ perspective: 1000 }}
    >
      <motion.div
        animate={{ rotateX: t.rx, rotateY: t.ry }}
        transition={{ type: "spring", stiffness: 120, damping: 12 }}
        className="relative clip-frame border border-clubborder bg-white p-2.5 shadow-[0_30px_80px_-40px_rgba(26,79,54,0.5)]"
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="overflow-hidden clip-frame">
          <img src={HERO_IMG} alt="Guests sharing a moment at a celebration" className="filmed w-full h-[340px] sm:h-[520px] object-cover" />
        </div>
        <div className="absolute bottom-4 left-4 bg-forest text-cream text-xs px-4 py-2 rounded-full font-sans tracking-wide" style={{ transform: "translateZ(40px)" }}>
          ● REC · Lower Mainland
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative pt-32 sm:pt-40 pb-20 px-5 sm:px-8">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="text-xs uppercase tracking-[0.3em] text-forest mb-6"
            >
              A video guestbook · est. for good stories
            </motion.p>
            <h1 className="font-sans font-extrabold tracking-tight text-ink text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem]">
              <MaskLines lines={["Your guests have", "more to say than", "a signature."]} delay={0.15} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-7 text-lg text-ink/70 max-w-xl leading-relaxed"
            >
              The Event Club sets up a vintage video booth at your wedding, birthday or celebration — so every guest leaves you a message you'll actually rewatch. Serving the Lower Mainland.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block"
              >
                <Link to="/book" data-testid="hero-book-cta" className="group rounded-full bg-forest text-cream px-8 py-4 text-base flex items-center gap-2 hover:bg-forest-deep transition-colors shadow-[0_14px_30px_-10px_rgba(26,79,54,0.6)]">
                  Check Your Date
                  <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </motion.div>
              <Link to="/how-it-works" className="rounded-full border border-forest/30 text-forest px-8 py-4 text-base hover:bg-forest/5 transition-colors">
                How it works
              </Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1 }}>
            <TiltFrame />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
          className="max-w-[1400px] mx-auto mt-14 flex justify-center lg:justify-start"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-1 text-forest/70"
            data-testid="hero-scroll-cue"
          >
            <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
            <ChevronDown size={20} />
          </motion.div>
        </motion.div>
      </section>

      {/* HOW IT WORKS STRIP */}
      <section className="px-5 sm:px-8 py-20 border-t border-clubborder">
        <div className="max-w-[1400px] mx-auto">
          <Reveal><p className="font-serif italic text-3xl sm:text-4xl text-forest mb-14">Three steps. Then you rewatch it for years.</p></Reveal>
          <div className="grid md:grid-cols-3 gap-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div className="border-t-2 border-forest pt-6">
                  <span className="font-serif text-7xl text-forest/25 leading-none">{s.n}</span>
                  <h3 className="font-sans font-bold text-xl mt-4 mb-2">{s.title}</h3>
                  <p className="text-ink/60 leading-relaxed">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE REEL + MARQUEE */}
      <Marquee />
      <section className="bg-forest px-5 sm:px-8 py-20 overflow-hidden">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <h2 className="font-serif text-cream text-4xl sm:text-5xl">The reel</h2>
              <p className="text-cream/60 max-w-sm">A 90-second taste of the messages people leave when nobody's rushing them.</p>
            </div>
          </Reveal>
          <Reveal>
            <VideoReel url={MEDIA.reelEmbedUrl} poster={MEDIA.reelPoster} length={MEDIA.reelLength} dark />
          </Reveal>
        </div>
      </section>
      <Marquee text="Press record · Speak from the heart" />

      {/* EVENT TYPES BENTO */}
      <section className="px-5 sm:px-8 py-24">
        <div className="max-w-[1400px] mx-auto">
          <Reveal><h2 className="font-sans font-extrabold tracking-tight text-4xl sm:text-5xl mb-3">Not just for weddings.</h2></Reveal>
          <Reveal delay={0.1}><p className="text-ink/60 max-w-xl mb-14 text-lg">Warm enough for a 60th birthday, cool enough for a product launch.</p></Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {EVENT_TYPES.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
                <div className="group relative overflow-hidden rounded-2xl border border-clubborder bg-clubmuted h-full min-h-[240px] p-8 flex flex-col justify-between transition-colors hover:bg-forest">
                  <img src={EVENT_IMG} alt="" className="filmed absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-25 transition-opacity duration-700" />
                  <span className="relative font-serif text-forest/40 group-hover:text-cream/50 text-2xl transition-colors">{e.tag}</span>
                  <div className="relative">
                    <h3 className="font-sans font-bold text-2xl mb-2 group-hover:text-cream transition-colors">{e.title}</h3>
                    <p className="text-ink/60 group-hover:text-cream/80 transition-colors max-w-md">{e.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING SNAPSHOT */}
      <section className="px-5 sm:px-8 py-24 bg-clubmuted border-y border-clubborder">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
            <Reveal><h2 className="font-sans font-extrabold tracking-tight text-4xl sm:text-5xl">Flat, honest pricing.</h2></Reveal>
            <Reveal delay={0.1}><Link to="/packages" className="text-forest flex items-center gap-1 hover:gap-2 transition-all">See full details & add-ons <ArrowUpRight size={18} /></Link></Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div className={`rounded-2xl p-8 h-full border ${p.featured ? "bg-forest text-cream border-forest" : "bg-cream border-clubborder"}`}>
                  {p.featured && <span className="inline-block text-xs uppercase tracking-widest bg-cream text-forest px-3 py-1 rounded-full mb-4">Most booked</span>}
                  <h3 className="font-sans font-bold text-2xl">{p.name}</h3>
                  <p className={`text-sm mt-1 ${p.featured ? "text-cream/70" : "text-ink/50"}`}>{p.tagline}</p>
                  <p className="font-serif text-6xl mt-6 mb-1">${p.price}</p>
                  <p className={`text-sm mb-6 ${p.featured ? "text-cream/70" : "text-ink/50"}`}>{p.hours} on-site</p>
                  <Link to="/book" className={`block text-center rounded-full py-3 text-sm font-medium transition-colors ${p.featured ? "bg-cream text-forest hover:bg-sand" : "bg-forest text-cream hover:bg-forest-deep"}`}>
                    Check availability
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsBlock />

      {/* SERVICE AREA */}
      <section className="px-5 sm:px-8 py-24 bg-forest text-cream">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-cream/50 mb-4 flex items-center gap-2"><MapPin size={14} /> Service area</p>
            <h2 className="font-serif text-4xl sm:text-6xl leading-tight mb-6">We come to you, across the Lower Mainland.</h2>
            <p className="text-cream/70 text-lg max-w-lg">Flat pricing within a 30km radius of our base. Beyond that, travel is $5/km — capped at $200, and always quoted upfront so there are no surprises.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-x-6">
              {SERVICE_AREAS.map((a, i) => (
                <div key={a} className="border-b border-cream/15 py-4 flex items-center gap-3">
                  <span className="font-serif text-cream/40 text-sm w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg">{a}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="px-5 sm:px-8 py-24">
        <div className="max-w-[1000px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <Reveal><h2 className="font-sans font-extrabold tracking-tight text-4xl sm:text-5xl">Good questions.</h2></Reveal>
            <Reveal delay={0.1}><Link to="/faq" className="text-forest flex items-center gap-1 hover:gap-2 transition-all">All FAQs <ArrowUpRight size={18} /></Link></Reveal>
          </div>
          <div>
            {FAQS.slice(0, 4).map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <div className="border-t border-clubborder py-6">
                  <h3 className="font-serif text-2xl mb-2">{f.q}</h3>
                  <p className="text-ink/60 leading-relaxed max-w-2xl">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-5 sm:px-8 py-24 bg-forest-deep">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-14">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-cream/50 mb-4">Check your date</p>
            <h2 className="font-serif text-cream text-5xl sm:text-6xl leading-tight">Let's get it on the calendar.</h2>
            <p className="text-cream/60 mt-6 text-lg max-w-md">Drop your details and I'll reply within 24 hours with availability and a quote.</p>
          </Reveal>
          <Reveal delay={0.15}><LeadForm dark /></Reveal>
        </div>
      </section>
    </div>
  );
}

function TestimonialsBlock() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  return (
    <section className="px-5 sm:px-8 py-24">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><p className="font-serif italic text-3xl sm:text-4xl text-forest mb-14 max-w-2xl">What people say once they've watched theirs back.</p></Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {items.map((t, i) => (
            <Reveal key={t.id || i} delay={i * 0.1}>
              <div className="border-t-2 border-forest pt-6 h-full">
                <Quote size={28} className="text-forest/30 mb-4" />
                <p className="font-serif text-2xl leading-snug mb-6">"{t.quote}"</p>
                <p className="font-sans font-bold">{t.name}</p>
                <p className="text-ink/50 text-sm">{t.event} · {t.city}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
